import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, MapPin, Search, Trash2, Star, Building2, MapPinned, Map as MapIcon } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import RowActions from '../../components/RowActions';
import {
  PlanClientStatusAvailable,
  PlanClientStatusEmpty,
} from '../../components/PlanClientStatus';
import { apiService } from '../../services/apiService';
import { formatDate } from '../../utils/helpers';
import { useToast } from '../../contexts/ToastContext';

const PlanManagement = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState({ open: false, client: null });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [planName, setPlanName] = useState('');
  const [planLocation, setPlanLocation] = useState('');
  const [planFloor, setPlanFloor] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [selectedClientPlans, setSelectedClientPlans] = useState(null);
  const [plansModal, setPlansModal] = useState({ open: false, client: null, plans: [] });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, planId: null, planName: '' });
  const [deleteClientConfirm, setDeleteClientConfirm] = useState({ open: false, client: null, plans: [] });
  const [editPlanModal, setEditPlanModal] = useState({
    open: false,
    plan: null,
    returnClient: null,
    loading: false,
  });
  const [editPlanForm, setEditPlanForm] = useState({ name: '', location: '', floor: '', isDefault: false });
  const [editPlanPreviewUrl, setEditPlanPreviewUrl] = useState(null);
  const [editPlanPreviewLoading, setEditPlanPreviewLoading] = useState(false);
  const [editPlanPreviewIsPdf, setEditPlanPreviewIsPdf] = useState(false);
  const [editReplaceFile, setEditReplaceFile] = useState(null);
  const [editReplacePreviewUrl, setEditReplacePreviewUrl] = useState(null);
  const [savingPlanEdit, setSavingPlanEdit] = useState(false);
  const [deletingPlanFile, setDeletingPlanFile] = useState(false);
  const [deletePlanFileConfirm, setDeletePlanFileConfirm] = useState(false);
  const [allPlans, setAllPlans] = useState([]);

  const clearEditPlanFilePreview = () => {
    if (editReplacePreviewUrl) {
      URL.revokeObjectURL(editReplacePreviewUrl);
    }
    setEditReplaceFile(null);
    setEditReplacePreviewUrl(null);
  };

  const clearEditPlanPreview = () => {
    if (editPlanPreviewUrl) {
      URL.revokeObjectURL(editPlanPreviewUrl);
    }
    setEditPlanPreviewUrl(null);
    setEditPlanPreviewIsPdf(false);
  };

  useEffect(() => {
    loadClients();
    loadAllPlans();
  }, []);

  useEffect(() => {
    filterClients();
  }, [searchQuery, clients]);

  useEffect(() => {
    if (!editPlanModal.open || !editPlanModal.plan?.id) {
      clearEditPlanPreview();
      return undefined;
    }

    if (!apiService.planHasFile(editPlanModal.plan)) {
      clearEditPlanPreview();
      return undefined;
    }

    let cancelled = false;
    let blobUrl = null;

    const loadPreview = async () => {
      setEditPlanPreviewLoading(true);
      clearEditPlanPreview();
      try {
        blobUrl = await apiService.fetchPlanFileBlob(editPlanModal.plan);
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        const fileName = apiService.getPlanFileName(editPlanModal.plan) || '';
        setEditPlanPreviewIsPdf(fileName.toLowerCase().endsWith('.pdf'));
        setEditPlanPreviewUrl(blobUrl);
      } catch (error) {
        console.error('Failed to load plan preview:', error);
        toast.error('Impossible d\'afficher le fichier du plan');
      } finally {
        if (!cancelled) setEditPlanPreviewLoading(false);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editPlanModal.open,
    editPlanModal.plan?.id,
    editPlanModal.plan?.fileName,
    editPlanModal.plan?.file_name,
    editPlanModal.plan?.fileUrl,
    editPlanModal.plan?.file_url,
  ]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await apiService.getClients();
      setClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClientPlans = async (clientId) => {
    try {
      const plans = await apiService.getClientPlans(clientId);
      return plans;
    } catch (error) {
      console.error('Failed to load plans:', error);
      return [];
    }
  };

  const loadAllPlans = async () => {
    try {
      const plans = await apiService.getPlans();
      setAllPlans(plans);
    } catch (error) {
      console.error('Failed to load all plans:', error);
    }
  };

  const filterClients = () => {
    if (!searchQuery) {
      setFilteredClients(clients);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = clients.filter(
      (client) =>
        client.name?.toLowerCase().includes(query) ||
        client.company?.toLowerCase().includes(query)
    );
    setFilteredClients(filtered);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Create preview URL for image files
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null); // For PDF files
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadModal.client) return;

    try {
      setUploading(true);
      await apiService.uploadPlan(
        uploadModal.client.id, 
        selectedFile,
        planName || 'Plan principal',
        planLocation,
        planFloor,
        isDefault
      );
      
      // Clean up
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      
      setUploadModal({ open: false, client: null });
      setSelectedFile(null);
      setPreviewUrl(null);
      setPlanName('');
      setPlanLocation('');
      setPlanFloor('');
      setIsDefault(false);
      await Promise.all([loadClients(), loadAllPlans()]);
      toast.success('Plan téléversé avec succès');
    } catch (error) {
      console.error('Failed to upload plan:', error);
      toast.error(error.message || 'Échec du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const handleViewPlans = async (client) => {
    const plans = await loadClientPlans(client.id);
    if (plans.length > 0) {
      // Find the default plan or use the first one
      const defaultPlan = plans.find(p => p.isDefault === 1) || plans[0];
      // Navigate directly to the placement page
      navigate(`/plans/place/${client.id}?planId=${defaultPlan.id}`);
    } else {
      // No plans, show modal (shouldn't happen since button only shows when has_plan is true)
      setPlansModal({ open: true, client, plans });
    }
  };
  
  const handleManagePlans = async (client) => {
    const plans = await loadClientPlans(client.id);
    setPlansModal({ open: true, client, plans });
  };

  const closeEditPlanModal = () => {
    const returnClient = editPlanModal.returnClient;
    clearEditPlanFilePreview();
    clearEditPlanPreview();
    setEditPlanModal({ open: false, plan: null, returnClient: null, loading: false });
    if (returnClient) {
      handleManagePlans(returnClient);
    }
  };

  const handleEditReplaceFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    clearEditPlanFilePreview();
    setEditReplaceFile(file);
    if (file.type.startsWith('image/')) {
      setEditReplacePreviewUrl(URL.createObjectURL(file));
    }
  };

  const normalizePlanForForm = (plan) => {
    if (!plan) {
      return { name: '', location: '', floor: '', isDefault: false };
    }
    return {
      name: plan.name ?? '',
      location: plan.location ?? '',
      floor: plan.floor ?? '',
      isDefault:
        plan.isDefault === 1 ||
        plan.isDefault === true ||
        plan.is_default === 1,
    };
  };

  const handleOpenEditPlan = async (plan, returnClientOverride = null) => {
    if (!plan?.id) {
      toast.error('Plan introuvable');
      return;
    }
    const returnClient = returnClientOverride ?? plansModal.client;
    const initialForm = normalizePlanForForm(plan);
    setEditPlanForm(initialForm);
    setEditPlanModal({ open: true, plan, returnClient, loading: false });
    setPlansModal({ open: false, client: null, plans: [] });

    try {
      const full = await apiService.getPlan(plan.id);
      const merged = { ...plan, ...full };
      setEditPlanForm(normalizePlanForForm(merged));
      setEditPlanModal((prev) => ({ ...prev, plan: merged, loading: false }));
    } catch (error) {
      console.error('Failed to load plan for edit:', error);
      toast.error('Détails complets indisponibles — valeurs affichées depuis la liste');
    }
  };

  const openUploadForClient = (client, { isDefault = false } = {}) => {
    setPlanName(client.name ? `Plan ${client.name}` : '');
    setPlanLocation(client.address || '');
    setPlanFloor('');
    setIsDefault(isDefault);
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadModal({ open: true, client });
  };

  const handleEditClientPlan = async (client) => {
    const plans = await loadClientPlans(client.id);
    if (plans.length === 0) {
      openUploadForClient(client, { isDefault: true });
      return;
    }
    if (plans.length > 1) {
      setPlansModal({ open: true, client, plans });
      toast.success('Choisissez un plan puis cliquez sur Modifier (crayon)');
      return;
    }
    await handleOpenEditPlan(plans[0], client);
  };

  const handleSavePlanEdit = async () => {
    if (!editPlanModal.plan) return;
    try {
      setSavingPlanEdit(true);
      const { isDefault, ...meta } = editPlanForm;
      if (editReplaceFile) {
        await apiService.replacePlanFile(editPlanModal.plan.id, editReplaceFile);
      }
      await apiService.updatePlan(editPlanModal.plan.id, meta);
      if (isDefault) {
        await apiService.setDefaultPlan(editPlanModal.plan.id);
      }
      await Promise.all([loadAllPlans(), loadClients()]);
      const returnClient = editPlanModal.returnClient;
      clearEditPlanFilePreview();
      clearEditPlanPreview();
      setEditPlanModal({ open: false, plan: null, returnClient: null, loading: false });
      if (returnClient) {
        const updatedPlans = await loadClientPlans(returnClient.id);
        setPlansModal({ open: true, client: returnClient, plans: updatedPlans });
      }
      toast.success('Plan mis à jour');
    } catch (error) {
      console.error('Failed to update plan:', error);
      toast.error(error.message || 'Échec de la mise à jour');
    } finally {
      setSavingPlanEdit(false);
    }
  };

  const handleDeletePlanFileFromEdit = async () => {
    if (!editPlanModal.plan?.id) return;
    try {
      setDeletingPlanFile(true);
      await apiService.deletePlanFile(editPlanModal.plan.id);
      clearEditPlanFilePreview();
      clearEditPlanPreview();
      const full = await apiService.getPlan(editPlanModal.plan.id);
      setEditPlanModal((prev) => ({
        ...prev,
        plan: { ...prev.plan, ...full },
      }));
      await Promise.all([loadClients(), loadAllPlans()]);
      const returnClient = editPlanModal.returnClient;
      if (returnClient) {
        const updatedPlans = await loadClientPlans(returnClient.id);
        setPlansModal((prev) =>
          prev.open ? { ...prev, plans: updatedPlans } : prev
        );
      }
      toast.success('Fichier du plan supprimé');
      setDeletePlanFileConfirm(false);
    } catch (error) {
      console.error('Failed to delete plan file:', error);
      toast.error(error.message || 'Échec de la suppression du fichier');
    } finally {
      setDeletingPlanFile(false);
    }
  };

  const handleDeleteClientPlans = async () => {
    try {
      for (const plan of deleteClientConfirm.plans) {
        await apiService.deletePlan(plan.id);
      }
      await Promise.all([loadClients(), loadAllPlans()]);
      setDeleteClientConfirm({ open: false, client: null, plans: [] });
      setPlansModal({ open: false, client: null, plans: [] });
      toast.success('Plans supprimés');
    } catch (error) {
      console.error('Failed to delete client plans:', error);
      toast.error(error.message || 'Échec de la suppression');
    }
  };

  const handleDeletePlan = async () => {
    try {
      await apiService.deletePlan(deleteConfirm.planId);
      const updatedPlans = await loadClientPlans(plansModal.client.id);
      setPlansModal({ ...plansModal, plans: updatedPlans });
      await loadClients();
      await loadAllPlans();
      setDeleteConfirm({ open: false, planId: null, planName: '' });
      toast.success('Plan supprimé');
    } catch (error) {
      console.error('Failed to delete plan:', error);
      toast.error(error.message || 'Échec de la suppression');
    }
  };

  const handleSetDefault = async (planId) => {
    try {
      await apiService.setDefaultPlan(planId);
      const updatedPlans = await loadClientPlans(plansModal.client.id);
      setPlansModal({ ...plansModal, plans: updatedPlans });
      toast.success('Plan par défaut mis à jour');
    } catch (error) {
      console.error('Failed to set default plan:', error);
      toast.error(error.message || 'Échec');
    }
  };

  const planCountByClientId = useMemo(() => {
    const counts = new Map();
    allPlans.forEach((plan) => {
      const clientId = plan.clientId ?? plan.client_id;
      if (clientId == null) return;
      counts.set(clientId, (counts.get(clientId) || 0) + 1);
    });
    return counts;
  }, [allPlans]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  // Calculate stats
  const totalClients = clients.length;
  const clientsWithPlans = clients.filter(c => c.planCount > 0 || c.has_plan || c.hasPlan).length;
  const clientsWithoutPlans = totalClients - clientsWithPlans;
  const totalPlans = allPlans.length;

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Plans</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          Gérez les plans de masse et placez les équipements
        </p>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-blue-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg shadow-blue-500/30">
                <MapIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Total Plans</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{totalPlans}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-green-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg sm:rounded-xl shadow-lg shadow-green-500/30">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-green-600 bg-green-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalClients > 0 ? Math.round((clientsWithPlans / totalClients) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Avec Plans</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{clientsWithPlans}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-amber-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg sm:rounded-xl shadow-lg shadow-amber-500/30">
                <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-amber-600 bg-amber-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalClients > 0 ? Math.round((clientsWithoutPlans / totalClients) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Sans Plans</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{clientsWithoutPlans}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-purple-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl shadow-lg shadow-purple-500/30">
                <MapPinned className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Total Clients</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{totalClients}</p>
          </div>
        </motion.div>
      </div>

      {/* Search */}
      <Card>
        <Input
          placeholder="Rechercher un client..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={Search}
        />
      </Card>

      {/* Clients Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
        {filteredClients.map((client, index) => (
          <motion.div
            key={client.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card hover className="h-full">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{client.name}</h3>
                  <p className="text-sm text-gray-500">{client.company}</p>
                </div>

                {(client.hasPlan || client.has_plan) ? (
                  <PlanClientStatusAvailable
                    planCount={planCountByClientId.get(client.id) || 0}
                    lastUploadedAt={
                      client.planUploadedAt || client.plan_uploaded_at
                    }
                  />
                ) : (
                  <PlanClientStatusEmpty />
                )}

                <div className="space-y-2 pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-end">
                    <RowActions
                      onView={() => handleManagePlans(client)}
                      onEdit={() => handleEditClientPlan(client)}
                      onDelete={async () => {
                        const plans = await loadClientPlans(client.id);
                        if (plans.length === 0) return;
                        setDeleteClientConfirm({ open: true, client, plans });
                      }}
                      disableDelete={!(client.hasPlan || client.has_plan)}
                    />
                  </div>
                  {(client.hasPlan || client.has_plan) && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        className="flex-1"
                        icon={MapPinned}
                        onClick={() => handleViewPlans(client)}
                      >
                        Placer équipements
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        icon={MapIcon}
                        onClick={() => handleManagePlans(client)}
                      >
                        Gérer plans
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Plans List Modal */}
      <Modal
        isOpen={plansModal.open}
        onClose={() => setPlansModal({ open: false, client: null, plans: [] })}
        title={`Plans de ${plansModal.client?.name}`}
      >
        <div className="space-y-4">
          {plansModal.plans.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucun plan disponible</p>
          ) : (
            plansModal.plans.map((plan) => (
              <div
                key={plan.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-red-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-semibold text-gray-900">{plan.name}</h4>
                      {(plan.isDefault === 1 || plan.isDefault === true) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Star className="w-3 h-3 mr-1" />
                          Par défaut
                        </span>
                      )}
                    </div>
                    
                    {plan.location && (
                      <p className="text-sm text-gray-600 flex items-center mb-1">
                        <Building2 className="w-4 h-4 mr-1" />
                        {plan.location}
                      </p>
                    )}
                    
                    {plan.floor && (
                      <p className="text-sm text-gray-600 flex items-center mb-1">
                        <MapPin className="w-4 h-4 mr-1" />
                        Étage: {plan.floor}
                      </p>
                    )}
                    
                    <p className="text-xs text-gray-500 mt-2">
                      Ajouté le {formatDate(plan.uploadedAt || plan.uploaded_at)}
                    </p>
                  </div>
                  
                  <div className="ml-4">
                    <RowActions
                      onView={() => navigate(`/plans/place/${plansModal.client.id}?planId=${plan.id}`)}
                      onEdit={() => handleOpenEditPlan(plan, plansModal.client)}
                      onDelete={() => setDeleteConfirm({ open: true, planId: plan.id, planName: plan.name })}
                    >
                      {plan.isDefault !== 1 && plan.isDefault !== true && (
                        <button
                          type="button"
                          onClick={() => handleSetDefault(plan.id)}
                          className="p-2 text-gray-400 hover:text-yellow-500 transition-colors"
                          title="Définir par défaut"
                        >
                          <Star className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate(`/plans/place/${plansModal.client.id}?planId=${plan.id}`)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Placer équipements"
                      >
                        <MapPin className="w-5 h-5" />
                      </button>
                    </RowActions>
                  </div>
                </div>
              </div>
            ))
          )}
          {plansModal.client && (
            <div className="pt-4 border-t flex justify-end">
              <Button
                variant="outline"
                icon={Upload}
                onClick={() => {
                  const client = plansModal.client;
                  setPlansModal({ open: false, client: null, plans: [] });
                  openUploadForClient(client, { isDefault: plansModal.plans.length === 0 });
                }}
              >
                Ajouter un plan
              </Button>
            </div>
          )}
        </div>
      </Modal>

      {/* Upload Modal */}
      <Modal
        isOpen={uploadModal.open}
        onClose={() => {
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          setUploadModal({ open: false, client: null });
          setSelectedFile(null);
          setPreviewUrl(null);
          setPlanName('');
          setPlanLocation('');
          setPlanFloor('');
          setIsDefault(false);
        }}
        title="Télécharger un plan"
      >
        <div className="space-y-4">
          <div>
            <p className="text-gray-600 mb-4">
              Client: <strong>{uploadModal.client?.name}</strong>
            </p>
            
            {/* Plan Info Fields */}
            <div className="space-y-3 mb-4">
              <Input
                label="Nom du plan*"
                placeholder="Ex: Bâtiment principal, Site Paris, Entrepôt..."
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
              />
              
              <Input
                label="Emplacement (optionnel)"
                placeholder="Ex: 123 Rue Example, Paris"
                value={planLocation}
                onChange={(e) => setPlanLocation(e.target.value)}
              />
              
              <Input
                label="Étage/Zone (optionnel)"
                placeholder="Ex: RDC, 1er étage, Zone A..."
                value={planFloor}
                onChange={(e) => setPlanFloor(e.target.value)}
              />
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <label htmlFor="isDefault" className="text-sm text-gray-700">
                  Définir comme plan par défaut
                </label>
              </div>
            </div>
            
            {!selectedFile ? (
              <div>
                <input
                  id="plan-file-upload"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label
                  htmlFor="plan-file-upload"
                  className="block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-500 transition-colors cursor-pointer"
                >
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-600 mb-2">
                    Cliquez pour sélectionner un fichier
                  </p>
                  <p className="text-xs text-gray-500">
                    Formats acceptés: JPG, PNG, PDF (max 10MB)
                  </p>
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Preview */}
                {previewUrl ? (
                  <div className="border-2 border-green-200 rounded-lg overflow-hidden bg-gray-50">
                    <img
                      src={previewUrl}
                      alt="Prévisualisation du plan"
                      className="w-full h-auto max-h-96 object-contain"
                    />
                  </div>
                ) : (
                  <div className="border-2 border-blue-200 rounded-lg p-8 text-center bg-blue-50">
                    <Upload className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                    <p className="text-sm text-blue-700 font-medium">Fichier PDF sélectionné</p>
                  </div>
                )}
                
                {/* File info */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Upload className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                      }
                      setSelectedFile(null);
                      setPreviewUrl(null);
                    }}
                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={() => {
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                }
                setUploadModal({ open: false, client: null });
                setSelectedFile(null);
                setPreviewUrl(null);
                setPlanName('');
                setPlanLocation('');
                setPlanFloor('');
                setIsDefault(false);
              }}
            >
              Annuler
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              loading={uploading}
              disabled={!selectedFile || !planName || uploading}
            >
              Télécharger
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Plan Modal */}
      <Modal
        isOpen={editPlanModal.open}
        onClose={closeEditPlanModal}
        title="Modifier le plan"
        stackPriority={1}
        size="lg"
      >
        <div className="space-y-4" key={editPlanModal.plan?.id ?? 'new'}>
          {editPlanModal.plan && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 space-y-1">
              <p>
                <span className="font-semibold text-gray-900">Créé le :</span>{' '}
                {formatDate(editPlanModal.plan.uploadedAt || editPlanModal.plan.uploaded_at) || '—'}
              </p>
              <p>
                <span className="font-semibold text-gray-900">Fichier :</span>{' '}
                {apiService.planHasFile(editPlanModal.plan)
                  ? apiService.getPlanFileName(editPlanModal.plan)
                  : 'Aucun fichier (métadonnées conservées)'}
                {apiService.planHasFile(editPlanModal.plan) && editPlanModal.plan.fileSize
                  ? ` (${(editPlanModal.plan.fileSize / (1024 * 1024)).toFixed(2)} Mo)`
                  : ''}
              </p>
            </div>
          )}

          <div>
            <p className="text-sm font-bold text-gray-700 mb-2">Fichier du plan (aperçu)</p>
            {editPlanPreviewLoading ? (
              <div className="flex justify-center py-10 border border-dashed border-gray-300 rounded-xl">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
              </div>
            ) : editReplacePreviewUrl ? (
              <div className="border-2 border-amber-200 rounded-xl overflow-hidden bg-gray-50">
                <img
                  src={editReplacePreviewUrl}
                  alt="Nouveau plan"
                  className="w-full h-auto max-h-80 object-contain"
                />
                <p className="text-xs text-amber-700 px-3 py-2 bg-amber-50">Nouveau fichier (enregistrer pour appliquer)</p>
              </div>
            ) : editReplaceFile && !editReplacePreviewUrl ? (
              <div className="border-2 border-amber-200 rounded-xl p-6 text-center bg-amber-50">
                <p className="text-sm font-medium text-amber-800">PDF sélectionné : {editReplaceFile.name}</p>
              </div>
            ) : editPlanPreviewUrl && editPlanPreviewIsPdf ? (
              <iframe
                title="Aperçu PDF du plan"
                src={editPlanPreviewUrl}
                className="w-full h-80 rounded-xl border border-gray-200 bg-white"
              />
            ) : editPlanPreviewUrl ? (
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                <img
                  src={editPlanPreviewUrl}
                  alt="Plan actuel"
                  className="w-full h-auto max-h-80 object-contain"
                />
              </div>
            ) : (
              <p className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-xl py-8 text-center">
                {apiService.planHasFile(editPlanModal.plan)
                  ? 'Aperçu indisponible'
                  : 'Aucun fichier — utilisez « Remplacer le fichier… » pour en ajouter un'}
              </p>
            )}
            <div className="flex flex-wrap gap-2 mt-3">
              {editPlanModal.plan && apiService.planHasFile(editPlanModal.plan) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(
                      `/plans/place/${editPlanModal.plan.clientId}?planId=${editPlanModal.plan.id}`
                    )
                  }
                >
                  Placer équipements sur ce plan
                </Button>
              )}
              {editPlanModal.plan && apiService.planHasFile(editPlanModal.plan) && (
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  icon={Trash2}
                  loading={deletingPlanFile}
                  disabled={deletingPlanFile || savingPlanEdit}
                  onClick={() => setDeletePlanFileConfirm(true)}
                >
                  Supprimer le fichier
                </Button>
              )}
              <label className="inline-flex cursor-pointer">
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={handleEditReplaceFileSelect}
                />
                <span className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-800">
                  Remplacer le fichier…
                </span>
              </label>
              {editReplaceFile && (
                <button
                  type="button"
                  onClick={clearEditPlanFilePreview}
                  className="text-sm text-red-600 hover:underline"
                >
                  Annuler le remplacement
                </button>
              )}
            </div>
          </div>
          <Input
            label="Nom du plan"
            name="editPlanName"
            value={editPlanForm.name}
            onChange={(e) => setEditPlanForm({ ...editPlanForm, name: e.target.value })}
            placeholder="Ex: Bâtiment principal, Entrepôt..."
            required
          />
          <Input
            label="Emplacement"
            name="editPlanLocation"
            value={editPlanForm.location}
            onChange={(e) => setEditPlanForm({ ...editPlanForm, location: e.target.value })}
            placeholder="Ex: 123 rue Example, Paris"
          />
          <Input
            label="Étage/Zone"
            name="editPlanFloor"
            value={editPlanForm.floor}
            onChange={(e) => setEditPlanForm({ ...editPlanForm, floor: e.target.value })}
            placeholder="Ex: RDC, 1er étage, Zone A..."
          />
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="editPlanIsDefault"
              checked={editPlanForm.isDefault}
              onChange={(e) => setEditPlanForm({ ...editPlanForm, isDefault: e.target.checked })}
              className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
            <label htmlFor="editPlanIsDefault" className="text-sm text-gray-700">
              Définir comme plan par défaut
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={closeEditPlanModal}>
              Annuler
            </Button>
            <Button
              variant="primary"
              onClick={handleSavePlanEdit}
              loading={savingPlanEdit}
              disabled={!editPlanForm.name.trim() || savingPlanEdit}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={deletePlanFileConfirm}
        stackPriority={2}
        closeOnConfirm={false}
        onClose={() => !deletingPlanFile && setDeletePlanFileConfirm(false)}
        onConfirm={handleDeletePlanFileFromEdit}
        title="Supprimer le fichier du plan"
        message={`Retirer le fichier « ${apiService.getPlanFileName(editPlanModal.plan) || 'plan'} » ? Le nom, l'emplacement et les autres informations du plan seront conservés, mais tous les marqueurs d'équipements sur ce plan seront supprimés. Vous pourrez téléverser un nouveau fichier ensuite.`}
        confirmText={deletingPlanFile ? 'Suppression…' : 'Supprimer le fichier'}
        cancelText="Annuler"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.open}
        stackPriority={1}
        onClose={() => setDeleteConfirm({ open: false, planId: null, planName: '' })}
        onConfirm={handleDeletePlan}
        title="Supprimer le plan"
        message={`Supprimer le plan "${deleteConfirm.planName}" ? Le fichier et tous les marqueurs d'équipements associés seront supprimés.`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />

      <ConfirmDialog
        isOpen={deleteClientConfirm.open}
        stackPriority={1}
        onClose={() => setDeleteClientConfirm({ open: false, client: null, plans: [] })}
        onConfirm={handleDeleteClientPlans}
        title="Supprimer tous les plans"
        message={`Supprimer les ${deleteClientConfirm.plans.length} plan(s) de "${deleteClientConfirm.client?.name}" ?`}
        confirmText="Supprimer tout"
        cancelText="Annuler"
      />
    </div>
  );
};

export default PlanManagement;
